import { proposalHref } from '../../../../../lib/proposalHref';
const DAY_MS = 86400000;
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
/** "2026-11-12" → "12 Nov". Calendar dates carry no zone, so no Date round trip. */ export function formatCalendarDate(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
    if (!m) return '';
    return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
}
function zoned(iso, timeZone, opts) {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return null;
    try {
        return new Intl.DateTimeFormat('en-US', {
            ...opts,
            timeZone: timeZone || undefined
        }).formatToParts(at);
    } catch  {
        // Unknown IANA name: fall back to the viewer's zone rather than hide the date.
        return new Intl.DateTimeFormat('en-US', opts).formatToParts(at);
    }
}
const part = (parts, type)=>parts.find((p)=>p.type === type)?.value ?? '';
/** RFC3339 → "12 Nov" in `timeZone` (viewer zone when empty). */ export function formatInstantDate(iso, timeZone) {
    const parts = zoned(iso, timeZone, {
        day: 'numeric',
        month: 'short'
    });
    return parts ? `${part(parts, 'day')} ${part(parts, 'month')}` : '';
}
/** RFC3339 → "12 Nov 2026, 5:00 PM EST" (zone abbreviation of `timeZone`, viewer zone when empty). */ export function formatDue(iso, timeZone) {
    const parts = zoned(iso, timeZone, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
    });
    if (!parts) return '';
    return `${part(parts, 'day')} ${part(parts, 'month')} ${part(parts, 'year')}, ${part(parts, 'hour')}:${part(parts, 'minute')} ${part(parts, 'dayPeriod')} ${part(parts, 'timeZoneName')}`;
}
/** $ millions with at most one decimal: 12_400_000_00 cents → "12.4". */ function millions(cents) {
    return String(Math.round(Number(cents) / 1e7) / 10);
}
/** Milestone pill only when the next milestone is 0–14 days away; due pill whenever due_at parses. */ export function headerPills(header, now = new Date()) {
    if (!header) return [];
    const pills = [];
    const at = Date.parse(header.nextMilestoneAt);
    if (header.nextMilestoneLabel && Number.isFinite(at)) {
        const days = Math.ceil((at - now.getTime()) / DAY_MS);
        if (days >= 0 && days <= 14) {
            pills.push({
                id: 'milestone',
                variant: 'info',
                text: `${header.nextMilestoneLabel} in ${days} ${days === 1 ? 'day' : 'days'}`
            });
        }
    }
    const due = formatDue(header.dueAt, header.dueTimezone);
    if (due) pills.push({
        id: 'due',
        variant: 'warning',
        text: `Due ${due}`
    });
    return pills;
}
export function headerSummary(header) {
    return header ? `${header.volumeCount} volumes · ${header.sectionCount} sections` : '';
}
export function toMetrics(home, projectId) {
    const out = [];
    const c = home.compliance;
    if (c) {
        out.push(c.hasBasis ? {
            id: 'compliance',
            label: 'Compliance addressed',
            tone: c.passFailOpen === 0 ? 'success' : 'warning',
            value: `${c.addressed} / ${c.total}`,
            note: `${c.total - c.addressed} open · ${c.passFailOpen} are pass/fail`,
            href: proposalHref(projectId, 'compliance')
        } : {
            id: 'compliance',
            label: 'Compliance addressed',
            tone: 'neutral',
            value: '—',
            muted: true,
            note: 'No compliance basis yet',
            href: proposalHref(projectId, 'compliance')
        });
    }
    const d = home.drafting;
    if (d) {
        out.push({
            id: 'drafting',
            label: 'Sections drafted',
            tone: 'info',
            value: `${d.drafted} / ${d.sections}`,
            note: `${d.noFirstDraft} have no first draft` + (d.withoutDocument > 0 ? ` · ${d.withoutDocument} have no document yet` : '')
        });
    }
    const p = home.price;
    if (p) {
        const hat = p.blackHat[0];
        let note;
        if (hat) {
            const range = `${hat.companyName} $${millions(hat.lowCents)}–${millions(hat.highCents)}M`;
            note = hat.position === 'inside' || hat.position === 'below' || hat.position === 'above' ? `${hat.position} ${range}` : `vs ${range}`;
        } else if (p.latestFrozenVersionNo === 0) {
            note = 'not frozen yet';
        }
        out.push({
            id: 'price',
            label: 'Price vs black hat',
            tone: 'warning',
            value: p.ourTotalCents === undefined ? '—' : `$${millions(p.ourTotalCents)}M`,
            note,
            href: proposalHref(projectId, 'sources/pricing')
        });
    }
    const r = home.nextReview;
    if (r) {
        out.push({
            id: 'review',
            label: 'Next review',
            tone: 'neutral',
            value: formatCalendarDate(r.date) || '—',
            note: r.reviewerCount > 0 ? `${r.label} · ${r.reviewerCount} reviewers` : r.label
        });
    }
    return out;
}
/** Server title is "{section number} {requirement text}"; explanation is "Missing" | "Partial". */ export function toPassFailRows(items) {
    return items.map((item)=>{
        const title = item.title.trim();
        const cut = title.search(/\s/);
        return {
            id: item.id,
            reference: cut < 0 ? title : title.slice(0, cut),
            label: cut < 0 ? '' : title.slice(cut).trim(),
            status: item.explanation === 'Partial' ? 'Partial' : 'Missing',
            href: item.href
        };
    });
}
export function toSectionRows(actions) {
    return actions.map((a)=>({
            id: a.id,
            volume: a.volumeName,
            title: a.title,
            href: `/proposal/${a.projectId}/v2/volume/${a.volumeId}/section/${a.sectionId}`,
            due: formatCalendarDate(a.internalDate) || '—'
        }));
}
const DECISION = {
    go: 'Go',
    no_go: 'No-go',
    defer: 'Defer'
};
/** Rail sections in order; a section with no facts is omitted. */ export function toRailSections(home) {
    const sections = [];
    const tz = home.header?.dueTimezone;
    if (home.briefing.length > 0) {
        sections.push({
            id: 'briefing',
            title: 'Briefing',
            facts: home.briefing.map((f)=>({
                    id: f.key,
                    label: f.label,
                    value: f.value,
                    href: f.href || undefined
                }))
        });
    }
    const milestones = home.milestones.map((m)=>({
            id: m.id,
            label: m.label,
            value: formatInstantDate(m.scheduledAt, tz),
            suffix: m.stateLabel === 'sent' ? ' · sent' : undefined
        }));
    if (home.header?.dueAt) {
        milestones.push({
            id: 'submit',
            label: 'Submit',
            value: formatInstantDate(home.header.dueAt, tz)
        });
    }
    if (milestones.length > 0) sections.push({
        id: 'milestones',
        title: 'Milestones',
        facts: milestones
    });
    const pu = home.pursuit;
    if (pu) {
        const facts = [];
        if (pu.link?.href) {
            facts.push({
                id: 'thread',
                label: 'Thread',
                value: pu.link.name || 'Open pursuit',
                href: pu.link.href
            });
        }
        const outcome = DECISION[pu.latestDecisionOutcome];
        if (outcome) {
            const when = formatInstantDate(pu.latestDecisionAt);
            facts.push({
                id: 'decision',
                label: pu.latestDecisionLabel || 'Decision',
                value: when ? `${outcome} · ${when}` : outcome
            });
        }
        if (pu.incumbentIsUs || pu.incumbentName) {
            facts.push({
                id: 'incumbent',
                label: 'Incumbent',
                value: pu.incumbentIsUs ? 'Us' : pu.incumbentName
            });
        }
        if (facts.length > 0) sections.push({
            id: 'pursuit',
            title: 'Pursuit',
            facts
        });
    }
    return sections;
}
